import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, PencilLine, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { itemApi } from '../../../api/endpoints';
import { queryKeys, weightUnitsQuery } from '../../../api/queries';
import type { DraftLine, InvoiceKind, Item, PreviewResult } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { AsyncComboBox } from '../../../ui/ComboBox';
import { DecimalField } from '../../../ui/DecimalField';
import { Money } from '../../../ui/Layout';
import { SelectField } from '../../../ui/Select';
import styles from '../Sales.module.css';

/** What a line shows about its item without reading the catalogue again. */
export interface LineInfo {
  name: string;
  code: string;
  unit: string;
  /** The item's catalogue price and the currency it is in, so an empty field can offer it (RI-05). */
  price: string | null;
  priceCurrency: string | null;
}

/**
 * Block 11 (notes-editor.md §3, AC-N5): a note line is a referenced line. Its fixed fields — the
 * item, the unit, the taxes and the export weight unit (the seven `NOTE_LOCKED_LINE_FIELDS`) — are
 * shown as PLAIN TEXT, never inputs, and the card carries an origin tag «من INV-… · البند n». Only
 * the quantity, price, discounts, value difference, fixed-tax amounts and export weight stay inputs.
 */
export interface NoteLineDisplay {
  /** The referenced invoice's number, for the origin tag. */
  invoiceNumber: string;
  /** The referenced line's number on that invoice. */
  lineNo: number;
  /** The locked item description (shown where the catalogue combo box is on an invoice). */
  description: string;
  /** The ETA item code (type + code), shown under the description. */
  code: string;
  /** The locked selling unit. */
  unit: string;
  /** On an export note, the locked weight unit; otherwise null. */
  weightUnit: string | null;
  /** The locked taxes, their identity fixed to the reference's (D-9). */
  taxes: {
    taxType: string;
    subType: string;
    organizationTaxId: string | null;
    kind: 'rate' | 'fixed' | string | null;
  }[];
}

export function IssueText({ text }: { text: string | undefined }) {
  if (!text) return null;
  return (
    <p className={styles.note} style={{ color: 'var(--color-danger-text)' }} role="note">
      {text}
    </p>
  );
}

/**
 * One invoice line as a card (step 3): item, quantity sold, unit price in the document currency,
 * discount (% or EGP), line total, tax chips with their amounts and fixed amounts, and more options
 * (items discount, value difference; on export the ETA weight unit and weight, open by default).
 */
export function LineEditor({
  orgId,
  index,
  line,
  kind,
  currency,
  info,
  computed,
  taxes,
  issueOf,
  canEditItem,
  note,
  onChange,
  onPickItem,
  onRemove,
  onEditItem,
  pick,
}: {
  orgId: string;
  index: number;
  line: DraftLine;
  kind: InvoiceKind;
  currency: string;
  info: LineInfo | undefined;
  computed: PreviewResult['lines'][number] | undefined;
  taxes: {
    id: string;
    taxType: string;
    taxSubtype: string;
    rate: string | null;
    nameAr: string;
    nameEn: string;
    isActive: boolean;
    kind: 'rate' | 'fixed';
  }[];
  issueOf: (path: string) => string | undefined;
  canEditItem: boolean;
  /** Block 11: when set, the line is a referenced line — its fixed fields render as text (AC-N5). */
  note?: NoteLineDisplay;
  onChange: (patch: Partial<DraftLine>) => void;
  onPickItem: (item: Item) => void;
  onRemove: () => void;
  onEditItem: () => void;
  pick: (ar: string | null | undefined, en: string) => string;
}) {
  const { t } = useI18n();
  const isNote = Boolean(note);
  const p = `lines.${index}`;
  const [more, setMore] = useState(
    kind === 'ei' || Boolean(line.itemsDiscount || line.valueDifference),
  );
  useEffect(() => {
    if (kind === 'ei') setMore(true);
  }, [kind]);
  // The mode is the line's own choice: it can be switched before a value is typed.
  const [discountMode, setDiscountMode] = useState<'rate' | 'amount'>(
    line.discount && 'amount' in line.discount ? 'amount' : 'rate',
  );
  useEffect(() => {
    if (line.discount) setDiscountMode('amount' in line.discount ? 'amount' : 'rate');
  }, [line.discount]);
  const discountValue = line.discount
    ? 'rate' in line.discount
      ? line.discount.rate
      : line.discount.amount
    : '';
  const taxIds = line.taxIds ?? [];
  // RI-05: the engine falls back to the item's catalogue price when the line has none, but only
  // when the item's currency is the document's. Offer it as the field's placeholder so a priced
  // line never looks unpriced.
  const cataloguePrice = info?.price && info.priceCurrency === currency ? info.price : undefined;
  const taxName = (id: string) => {
    const x = taxes.find((tx) => tx.id === id);
    return x ? `${pick(x.nameAr, x.nameEn)} (${x.taxType}/${x.taxSubtype})` : id;
  };
  return (
    <div className={styles.line} data-testid={`line-${index}`}>
      <span className={styles.lineIndex}>{t.editor.lines.lineLabel(index + 1)}</span>
      {note ? (
        <span className={styles.originTag} data-testid={`${p}.origin`}>
          {t.editor.note.fromInvoice(note.invoiceNumber)} · {t.editor.note.fromLine(note.lineNo)}
        </span>
      ) : null}
      {note ? (
        <div className={styles.lineItem} data-field={`${p}.itemId`} data-readonly="true">
          <span className={styles.readOnlyLabel}>{t.editor.lines.item}</span>
          <span className={styles.readOnlyValue}>
            <UserText>{note.description}</UserText>
          </span>
          {note.code.trim() || note.unit ? (
            <span className={styles.subtle}>
              {[note.code.trim(), note.unit].filter(Boolean).join(' · ')}
            </span>
          ) : null}
        </div>
      ) : (
        <AsyncComboBox<Item>
          label={t.editor.lines.item}
          className={styles.lineItem}
          fieldName={`${p}.itemId`}
          value={line.itemId ?? null}
          selectedItem={
            line.itemId
              ? ({ id: line.itemId, nameAr: info?.name ?? '…', internalCode: '' } as Item)
              : null
          }
          onChange={(item) => item && onPickItem(item)}
          queryFor={(q) => ({
            queryKey: [...queryKeys.org(orgId), 'items', 'picker', q],
            queryFn: async ({ signal }) =>
              (await itemApi.list(orgId, { q, limit: 20 }, signal)).items,
          })}
          getKey={(i) => i.id}
          getLabel={(i) => i.nameAr}
          description={info ? `${info.code} · ${info.unit}` : undefined}
          errorMessage={issueOf(`${p}.itemId`)}
        />
      )}
      <DecimalField
        label={t.editor.lines.quantity}
        fieldName={`${p}.quantity`}
        value={line.quantity ?? ''}
        onChange={(v) => onChange({ quantity: v })}
        errorMessage={issueOf(`${p}.quantity`)}
      />
      <DecimalField
        label={t.editor.lines.unitPrice(currency)}
        fieldName={`${p}.unitPrice`}
        value={line.unitPrice ?? ''}
        onChange={(v) => onChange({ unitPrice: v })}
        placeholder={cataloguePrice}
        errorMessage={issueOf(`${p}.unitPrice`) ?? issueOf(`${p}.amountSold`)}
      />
      <DecimalField
        label={t.editor.lines.discount}
        fieldName={`${p}.discount`}
        value={discountValue}
        onChange={(v) =>
          onChange({
            discount: v ? (discountMode === 'rate' ? { rate: v } : { amount: v }) : undefined,
          })
        }
        suffix={
          <button
            type="button"
            className={styles.chipSelect}
            style={{ borderStyle: 'solid' }}
            aria-label={t.editor.lines.discountMode}
            data-testid={`${p}.discountMode`}
            onClick={() => {
              const next = discountMode === 'rate' ? 'amount' : 'rate';
              setDiscountMode(next);
              if (discountValue)
                onChange({
                  discount: next === 'rate' ? { rate: discountValue } : { amount: discountValue },
                });
            }}
          >
            {discountMode === 'rate'
              ? t.editor.lines.discountPercent
              : t.editor.lines.discountAmount}
          </button>
        }
        errorMessage={issueOf(`${p}.discount.rate`) ?? issueOf(`${p}.discount.amount`)}
      />
      <div className={styles.lineTotal}>
        <span className={styles.subtle}>{t.editor.lines.total}</span>
        <Money value={computed?.total} currency="EGP" testId={`${p}.total`} />
      </div>
      <div className={styles.lineTools}>
        <Button
          size="sm"
          variant="ghost"
          onPress={() => setMore(!more)}
          aria-label={more ? t.editor.lines.less : t.editor.lines.more}
          testId={`${p}.more`}
        >
          {more ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </Button>
        {!isNote && canEditItem && line.itemId ? (
          <Button
            size="sm"
            variant="ghost"
            onPress={onEditItem}
            aria-label={t.editor.lines.editItem}
            testId={`${p}.editItem`}
          >
            <PencilLine size={15} />
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onPress={onRemove}
          aria-label={t.editor.lines.remove}
          testId={`${p}.remove`}
        >
          <Trash2 size={15} />
        </Button>
      </div>
      {note ? (
        <div className={styles.chips} data-field={`${p}.taxes`} data-readonly="true">
          <span className={styles.subtle}>{t.editor.lines.taxes}:</span>
          {note.taxes.length === 0 ? (
            <span className={styles.subtle}>{t.editor.lines.noTaxes}</span>
          ) : null}
          {note.taxes.map((tax, j) => {
            const orgTax = tax.organizationTaxId
              ? taxes.find((x) => x.id === tax.organizationTaxId)
              : undefined;
            const label = orgTax
              ? `${pick(orgTax.nameAr, orgTax.nameEn)} (${orgTax.taxType}/${orgTax.taxSubtype})`
              : `${tax.taxType}/${tax.subType}`;
            const amount = computed?.taxes[j]?.amount;
            // A fixed tax keeps its amount editable, keyed by the organization tax it maps to.
            const fixedKey = tax.kind === 'fixed' ? tax.organizationTaxId : null;
            return (
              <span
                key={tax.organizationTaxId ?? `${tax.taxType}/${tax.subType}/${j}`}
                className={styles.chip}
                data-testid={`${p}.tax-${tax.taxType}`}
              >
                {label}
                {amount ? (
                  <span className={styles.chipAmount}>
                    = <Money value={amount} currency="EGP" />
                  </span>
                ) : null}
                {fixedKey ? (
                  <input
                    aria-label={t.editor.lines.fixedAmount(label)}
                    className={styles.chipSelect}
                    style={{ inlineSize: '6rem' }}
                    inputMode="decimal"
                    value={line.fixedTaxAmounts?.[fixedKey] ?? ''}
                    onChange={(e) =>
                      onChange({
                        fixedTaxAmounts: {
                          ...(line.fixedTaxAmounts ?? {}),
                          [fixedKey]: e.target.value,
                        },
                      })
                    }
                  />
                ) : null}
              </span>
            );
          })}
        </div>
      ) : (
        <div className={styles.chips} data-field={`${p}.taxIds`}>
          <span className={styles.subtle}>{t.editor.lines.taxes}:</span>
          {taxIds.length === 0 ? (
            <span className={styles.subtle}>{t.editor.lines.noTaxes}</span>
          ) : null}
          {taxIds.map((id, j) => {
            const tax = taxes.find((x) => x.id === id);
            const amount = computed?.taxes[j]?.amount;
            return (
              <span key={id} className={styles.chip} data-testid={`${p}.tax-${tax?.taxType ?? id}`}>
                {taxName(id)}
                {amount ? (
                  <span className={styles.chipAmount}>
                    = <Money value={amount} currency="EGP" />
                  </span>
                ) : null}
                {tax?.kind === 'fixed' ? (
                  <input
                    aria-label={t.editor.lines.fixedAmount(taxName(id))}
                    className={styles.chipSelect}
                    style={{ inlineSize: '6rem' }}
                    inputMode="decimal"
                    value={line.fixedTaxAmounts?.[id] ?? ''}
                    onChange={(e) =>
                      onChange({
                        fixedTaxAmounts: { ...(line.fixedTaxAmounts ?? {}), [id]: e.target.value },
                      })
                    }
                  />
                ) : null}
                <button
                  type="button"
                  aria-label={t.editor.lines.removeTax(taxName(id))}
                  onClick={() => onChange({ taxIds: taxIds.filter((x) => x !== id) })}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          <select
            className={styles.chipSelect}
            aria-label={t.editor.lines.addTax}
            value=""
            data-testid={`${p}.addTax`}
            onChange={(e) => e.target.value && onChange({ taxIds: [...taxIds, e.target.value] })}
          >
            <option value="">+ {t.editor.lines.addTax}</option>
            {taxes
              .filter((x) => x.isActive && !taxIds.includes(x.id))
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {taxName(x.id)}
                </option>
              ))}
          </select>
          <IssueText text={issueOf(`${p}.taxIds`)} />
        </div>
      )}
      {more ? (
        <div className={styles.lineMore}>
          <DecimalField
            label={t.editor.lines.itemsDiscount}
            fieldName={`${p}.itemsDiscount`}
            value={line.itemsDiscount ?? ''}
            onChange={(v) => onChange({ itemsDiscount: v })}
            isOptional
          />
          <DecimalField
            label={t.editor.lines.valueDifference}
            fieldName={`${p}.valueDifference`}
            value={line.valueDifference ?? ''}
            onChange={(v) => onChange({ valueDifference: v })}
            isOptional
          />
          {kind === 'ei' ? (
            <>
              {note ? (
                <div
                  className={styles.readOnlyField}
                  data-field={`${p}.weightUnitType`}
                  data-readonly="true"
                >
                  <span className={styles.readOnlyLabel}>{t.editor.lines.weightUnit}</span>
                  <span className={styles.readOnlyValue}>{note.weightUnit ?? '—'}</span>
                </div>
              ) : (
                <WeightUnitField
                  index={index}
                  value={line.weightUnitType ?? null}
                  onChange={(v) => onChange({ weightUnitType: v ?? undefined })}
                  errorMessage={issueOf(`${p}.weightUnitType`)}
                />
              )}
              <DecimalField
                label={t.editor.lines.weightQuantity}
                fieldName={`${p}.weightQuantity`}
                value={line.weightQuantity ?? ''}
                onChange={(v) => onChange({ weightQuantity: v })}
                isRequired
                errorMessage={issueOf(`${p}.weightQuantity`)}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** ETA weight units from the official reference table (KGM = kilogram; "KG" in the unit table is a keg). */
function WeightUnitField({
  index,
  value,
  onChange,
  errorMessage,
}: {
  index: number;
  value: string | null;
  onChange: (v: string | null) => void;
  errorMessage: string | undefined;
}) {
  const { t, pick } = useI18n();
  const units = useQuery(weightUnitsQuery);
  return (
    <SelectField
      label={t.editor.lines.weightUnit}
      fieldName={`lines.${index}.weightUnitType`}
      value={value}
      onChange={onChange}
      options={(units.data ?? []).map((u) => ({
        id: u.code,
        label: `${u.code} — ${pick(u.nameAr, u.nameEn)}`,
      }))}
      isRequired
      errorMessage={errorMessage}
    />
  );
}
